import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { fadeInUpVariants } from '../../hooks/useScrollAnimation';
import { FaEnvelope, FaGithub, FaLinkedin, FaFacebook, FaInstagram } from 'react-icons/fa';
import { SiUpwork } from 'react-icons/si';
import emailjs from '@emailjs/browser';

interface IPLimits {
  [key: string]: string;
}

interface IPResponse {
  ip: string;
}

interface IValidationMessage {
  id: string;
  provider: string;
  model: string;
  object: string;
  created: number;
  choices: IValidationChoice[];
  system_fingerprint: string | null;
  usage: IValidationUsage;
}

interface IValidationChoice {
  logprobs: null;
  finish_reason: string;
  index: number;
  message: {
    role: string;
    content: string;
    refusal: string;
  };
}

interface IValidationUsage {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
}

const ContactSection = styled.section`
  padding: ${({ theme }) => theme.spacing.section} ${({ theme }) => theme.spacing.xl};
  max-width: ${({ theme }) => theme.maxWidth};
  margin: 0 auto;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.xxxl} ${({ theme }) => theme.spacing.lg};
  }
`;

const SectionHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xxxl};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-bottom: ${({ theme }) => theme.spacing.xxl};
  }
`;

const Kicker = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.primary};
  letter-spacing: 2px;
  text-transform: uppercase;
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  font-family: ${({ theme }) => theme.fonts.display};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.1;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionDescription = styled(motion.p)`
  font-size: 1.125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 600px;
  line-height: 1.6;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1rem;
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xxxl};
  align-items: start;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.xxl};
  }
`;

const FormColumn = styled(motion.div)`
  position: relative;
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.surface};
  padding: ${({ theme }) => theme.spacing.xxl};
  border: 2px solid ${({ theme }) => theme.colors.border};
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.lg};
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const FormLabel = styled.label`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.primary};
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  display: block;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const InputBase = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 1rem;
  transition: border-color 0.3s ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
    opacity: 0.5;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    font-size: 0.9375rem;
  }
`;

const Input = styled(InputBase)``;

const TextArea = styled(InputBase).attrs({ as: 'textarea' })`
  min-height: 150px;
  resize: vertical;
  font-family: ${({ theme }) => theme.fonts.body};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-height: 120px;
  }
`;

const SubmitButton = styled(motion.button)`
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.xxl};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.background};
  border: none;
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background: ${({ theme }) => theme.colors.text};
    transform: translate(-50%, -50%);
    transition:
      width 0.3s ease,
      height 0.3s ease;
  }

  &:hover::before {
    width: 100%;
    height: 100%;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.background};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  span {
    position: relative;
    z-index: 1;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

const LoadingSpinner = styled(motion.span)`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid ${({ theme }) => theme.colors.background};
  border-bottom-color: transparent;
  border-radius: 50%;
  margin-right: ${({ theme }) => theme.spacing.sm};
  vertical-align: middle;
`;

const StatusMessage = styled(motion.p)<{ $status: 'success' | 'error' | 'cooldown' | 'invalid' }>`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.875rem;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid;
  margin-top: ${({ theme }) => theme.spacing.md};
  border-color: ${({ theme, $status }) => {
    switch ($status) {
      case 'success':
        return theme.colors.primary;
      case 'error':
      case 'invalid':
        return '#ff4444';
      case 'cooldown':
        return '#ffaa00';
      default:
        return theme.colors.border;
    }
  }};
  color: ${({ theme, $status }) => {
    switch ($status) {
      case 'success':
        return theme.colors.primary;
      case 'error':
      case 'invalid':
        return '#ff4444';
      case 'cooldown':
        return '#ffaa00';
      default:
        return theme.colors.text;
    }
  }};
  background: ${({ theme, $status }) => {
    switch ($status) {
      case 'success':
        return `${theme.colors.primary}11`;
      case 'error':
      case 'invalid':
        return '#ff444411';
      case 'cooldown':
        return '#ffaa0011';
      default:
        return theme.colors.surface;
    }
  }};
`;

const InfoColumn = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxl};
`;

const InfoBlock = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  transition: border-color 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const InfoTitle = styled.h3`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.primary};
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const InfoText = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.7;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 0.9375rem;
  }
`;

const SocialLinks = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(4, 1fr);
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const SocialLink = styled(motion.a)`
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.primary};
    transform: translateY(100%);
    transition: transform 0.3s ease;
  }

  svg {
    position: relative;
    z-index: 1;
    transition: color 0.3s ease;
  }

  &:hover::before {
    transform: translateY(0);
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:hover svg {
    color: ${({ theme }) => theme.colors.background};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.25rem;
  }
`;

const DirectContact = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const ContactLink = styled(motion.a)`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.text};
  text-decoration: none;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  cursor: pointer;
  position: relative;
  z-index: 1;

  svg {
    color: ${({ theme }) => theme.colors.primary};
    font-size: 1.125rem;
    pointer-events: none;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.surface};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 0.8125rem;
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  }
`;

const Contact: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    'idle' | 'success' | 'error' | 'cooldown' | 'invalid'
  >('idle');
  const [userIP, setUserIP] = useState<string>('');
  const form = useRef<HTMLFormElement>(null);

  const ONE_DAY_MS = 24 * 60 * 60 * 1000;

  useEffect(() => {
    void fetch('https://api.ipify.org?format=json')
      .then(response => response.json() as Promise<IPResponse>)
      .then(data => {
        setUserIP(data.ip);
      })
      .catch(error => {
        console.error('Error fetching IP:', error);
      });
  }, []);

  const checkIPLimit = (ip: string): boolean => {
    if (!ip) return true;

    const ipLimits = JSON.parse(localStorage.getItem('ipLimits') || '{}') as IPLimits;
    const lastSentTime = ipLimits[ip];

    if (!lastSentTime) return true;

    const timeSinceLastEmail = Date.now() - parseInt(lastSentTime, 10);
    return timeSinceLastEmail >= ONE_DAY_MS;
  };

  const updateIPLimit = (ip: string): void => {
    if (!ip) return;

    const ipLimits = JSON.parse(localStorage.getItem('ipLimits') || '{}') as IPLimits;
    ipLimits[ip] = Date.now().toString();
    localStorage.setItem('ipLimits', JSON.stringify(ipLimits));
  };

  const getTimeUntilNextEmail = (ip: string): string => {
    if (!ip) return 'Please wait 24 hours before sending another message.';

    const ipLimits = JSON.parse(localStorage.getItem('ipLimits') || '{}') as IPLimits;
    const lastSentTime = ipLimits[ip];

    if (!lastSentTime) return '';

    const nextAvailableTime = parseInt(lastSentTime, 10) + ONE_DAY_MS;
    const now = Date.now();
    const hoursLeft = Math.ceil((nextAvailableTime - now) / (1000 * 60 * 60));

    return `Please wait ${hoursLeft} hour${hoursLeft !== 1 ? 's' : ''} before sending another message.`;
  };

  const validateMessage = async (message: string): Promise<boolean> => {
    try {
      const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          Authorization: `Bearer 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`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'google/gemini-2.0-flash-001',
          messages: [
            {
              role: 'user',
              content: `Evaluate if the following text is meaningful in any language and respond with only 'yes' or 'no':
'${message}'
Consider these examples:
- 'hello' would be 'yes'
- 'asdfgh' would be 'no'
- 'im happy' would be 'yes'
- 'the bjkld' would be 'no'`,
            },
          ],
        }),
      });

      const data = (await response.json()) as IValidationMessage;
      const answer = data.choices[0].message.content.toLowerCase();
      return answer.includes('yes');
    } catch (error) {
      console.error('Error validating message:', error);
      return true;
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const submitForm = async () => {
      e.preventDefault();
      setIsSubmitting(true);

      if (!form.current) return;

      if (!checkIPLimit(userIP)) {
        setSubmitStatus('cooldown');
        setIsSubmitting(false);
        return;
      }

      setSubmitStatus('idle');

      try {
        const formData = new FormData(form.current);
        const message = formData.get('message') as string;

        const isValid = await validateMessage(message);
        if (!isValid) {
          setIsSubmitting(false);
          setSubmitStatus('invalid');
          return;
        }

        const result = await emailjs.sendForm(
          'service_1k02em7',
          'template_iso19eo',
          form.current,
          'grcnTYQrnadkkE0QI'
        );

        if (result.text === 'OK') {
          setSubmitStatus('success');
          updateIPLimit(userIP);
          if (form.current) {
            form.current.reset();
          }
        }
      } catch (error) {
        console.error('Error sending email:', error);
        setSubmitStatus('error');
      } finally {
        setIsSubmitting(false);
      }
    };

    void submitForm().catch(error => {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      setIsSubmitting(false);
    });
  };

  return (
    <ContactSection id="contact">
      <SectionHeader>
        <Kicker>Connect</Kicker>
        <SectionTitle
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={fadeInUpVariants}
        >
          Let&apos;s Work Together
        </SectionTitle>
        <SectionDescription
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={fadeInUpVariants}
          custom={0.1}
        >
          You know I can&apos;t show everything here, right? 🫣 Send me a message or connect with me
          on socials so we can work on your next big thing!
        </SectionDescription>
      </SectionHeader>

      <ContentWrapper>
        <FormColumn
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={fadeInUpVariants}
          custom={0.2}
        >
          <ContactForm ref={form} onSubmit={handleSubmit}>
            <InputWrapper>
              <FormLabel htmlFor="from_name">Name</FormLabel>
              <Input type="text" id="from_name" name="from_name" placeholder="Your Name" required />
            </InputWrapper>

            <InputWrapper>
              <FormLabel htmlFor="user_email">Email</FormLabel>
              <Input
                type="email"
                id="user_email"
                name="user_email"
                placeholder="your.email@example.com"
                required
              />
            </InputWrapper>

            <InputWrapper>
              <FormLabel htmlFor="subject">Subject</FormLabel>
              <Input
                type="text"
                id="subject"
                name="subject"
                placeholder="What's this about?"
                required
              />
            </InputWrapper>

            <InputWrapper>
              <FormLabel htmlFor="message">Message</FormLabel>
              <TextArea
                id="message"
                name="message"
                placeholder="Tell me about your project..."
                required
              />
            </InputWrapper>

            <SubmitButton
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
            >
              <span>
                {isSubmitting && (
                  <LoadingSpinner
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: 'linear',
                    }}
                  />
                )}
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </span>
            </SubmitButton>

            {submitStatus === 'success' && (
              <StatusMessage
                $status="success"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                ✓ Message sent successfully!
              </StatusMessage>
            )}
            {submitStatus === 'error' && (
              <StatusMessage
                $status="error"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                ✗ Failed to send message. Please try again.
              </StatusMessage>
            )}
            {submitStatus === 'cooldown' && (
              <StatusMessage
                $status="cooldown"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                ⚠ {getTimeUntilNextEmail(userIP)}
              </StatusMessage>
            )}
            {submitStatus === 'invalid' && (
              <StatusMessage
                $status="invalid"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                ⚠ Hmmmm, it seems there might be spelling issues or nonsensical words in your
                message
              </StatusMessage>
            )}
          </ContactForm>
        </FormColumn>

        <InfoColumn
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={fadeInUpVariants}
          custom={0.3}
        >
          <InfoBlock>
            <InfoTitle>Direct Contact</InfoTitle>
            <DirectContact>
              <ContactLink
                href="https://mail.google.com/mail/?view=cm&fs=1&to=khanhlq21011999@gmail.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <FaEnvelope />
                khanhlq21011999@gmail.com
              </ContactLink>
            </DirectContact>
          </InfoBlock>

          <InfoBlock>
            <InfoTitle>Social Links</InfoTitle>
            <SocialLinks>
              <SocialLink
                href="https://github.com/khanh21011999"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaGithub />
              </SocialLink>
              <SocialLink
                href="https://www.linkedin.com/in/l%C3%AA-qu%E1%BB%91c-kh%C3%A1nh-364543231/"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaLinkedin />
              </SocialLink>
              <SocialLink
                href="https://www.facebook.com/quockhanh21021999/"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaFacebook />
              </SocialLink>
              <SocialLink
                href="https://www.instagram.com/without_jklmnopqrst/"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaInstagram />
              </SocialLink>
              <SocialLink
                href="https://www.upwork.com/freelancers/~019f04d8ea920eb2a0"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SiUpwork />
              </SocialLink>
              <SocialLink
                href="https://mail.google.com/mail/?view=cm&fs=1&to=khanhlq21011999@gmail.com"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaEnvelope />
              </SocialLink>
            </SocialLinks>
          </InfoBlock>

          <InfoBlock>
            <InfoTitle>Availability</InfoTitle>
            <InfoText>
              Currently available for freelance projects and full-time opportunities.
            </InfoText>
            <InfoText>Response time: Usually within 24 hours.</InfoText>
          </InfoBlock>
        </InfoColumn>
      </ContentWrapper>
    </ContactSection>
  );
};

export default Contact;
